import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import {
    Question,
    QuestionGroup,
    QuestionType,
    StyledRowSelectorOptions,
    IfNeedsAdditional,
    Legend,
    ShowIf,
    FormValidationMetadata,
    alertModal,
    loginAlertModal,
    FileOrFormQuestion,
    validEntityType,
    stateSelectorValues,
    newBorrowerOptionText,
} from '@apply/ui';
import * as Store from '@apply/store';
import { confirmModal } from '@apply/ui';

import { Header } from './Header';
import { Callout } from './Callout';
import { useAuthentication } from './LoginPage';
import { EntityListResult } from 'libs/store/src/cross';
import { LoanType, LoanTypeOptions } from '@apply/constants';
import { ConfirmModal } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { HomePageLink } from './HomePageLink';

export interface Actions {
    onChangeLoanType?: (newvalue: LoanType | undefined) => void;
    onChangeConfirmEntity?: (newvalue: number | undefined) => void;
    onChangeFirstLoadApplicationWithBfi?: (newvalue: boolean | undefined) => void;
    onChangeHowDidYouHear?: (newvalue: string | undefined) => void;
    onChangeInvolvedInJudgement?: (newvalue: boolean | undefined) => void;
    onChangeDelinquentOnDebts?: (newvalue: boolean | undefined) => void;
    onChangeDelinquentOnTaxes?: (newvalue: boolean | undefined) => void;
    onChangeFinanciallyWorseOff?: (newvalue: boolean | undefined) => void;
    onChangeCurrentlyQuestionsExplanations?: (newvalue: string | undefined) => void;
    onChangeDefendantInLegalActions?: (newvalue: boolean | undefined) => void;
    onChangeUsingPropertyManagement?: (newvalue: boolean | undefined) => void;
    onChangePropertyManagementCompanyName?: (newvalue: string | undefined) => void;
    onChangePropertyManagementCompanyWebsite?: (newvalue: string | undefined) => void;
    onChangePropertyManagementCompanyContactName?: (newvalue: Store.BorrowerStore.Types.FullName) => void;
    onChangePropertyManagementCompanyContactEmail?: (newvalue: string | undefined) => void;
    onChangePropertyManagementCompanyContactPhone?: (newvalue: string | undefined) => void;
    onChangeContactInfoSameAsPropertyContact?: (newvalue: boolean | undefined) => void;
    onChangePrincipalName?: (newvalue: Store.BorrowerStore.Types.FullName) => void;
    onChangePrincipalEmail?: (newvalue: string | undefined) => void;
    onChangePrincipalSecondaryEmail?: (newvalue: string | undefined) => void;
    onChangePrincipalPhone?: (newvalue: string | undefined) => void;
    onChangeBorrowerId?: (newvalue: number | undefined) => void;
    onChangeEntityId: (newvalue: number | undefined) => void;
    onChangeEntityName?: (newvalue: string | undefined) => void;
    onChangeEntityStateIncorporated?: (newvalue: string | undefined) => void;
    onChangeMasterNoteId?: (newvalue: number | undefined) => void;
    onChangeEntityAddressStreet1?: (newvalue: string | undefined) => void;
    onChangeEntityAddressStreet2?: (newvalue: string | undefined) => void;
    onChangeEntityAddressCity?: (newvalue: string | undefined) => void;
    onChangeEntityAddressState?: (newvalue: string | undefined) => void;
    onChangeEntityAddressZipcode?: (newvalue: string | undefined) => void;
    loadStateFromApi?: (apiId: string, token: string) => Promise<boolean | undefined>;
    loadBorrowerStateFromUserApplications?: (email: string) => Promise<unknown>;
    saveStateToApi?: (apiId: string, complete: boolean, token: string) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    validateEntity?: (id: number, name: string, state: string) => Promise<Store.CrossStore.ValidationMessagesResult>;
    getUserApplications?: (email: string) => Promise<unknown>;
    getEntityList?: () => Promise<Store.CrossStore.EntityListResult>;
    get?: (email: string) => Promise<unknown>;
    onChangeGuestAcknowledgedNoPersonalLoans?: (newvalue: boolean | undefined) => void;
    onChangeManagementAgreement: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    uploadAttachment?: (
        apiId: string,
        loanType: LoanType,
        propertyGuid: string,
        fileType: Store.PropertiesStore.Types.AttachmentType,
        file: File,
        updater: Store.PropertiesStore.Actions.AttachmentUpdater,
        token: string
    ) => Promise<string>;
}

export interface BorrowerPageProps {
    borrower?: Store.BorrowerStore.Types.BorrowerState;
    dispatch?: Store.ApplyDispatchType;
    actions?: Actions;
    completedApplications?: Store.UserStore.Types.UserApplication[];
}

const { Actions } = Store.BorrowerStore;

const entityTypes = [{ id: '', name: '' }, ...Store.BorrowerStore.Types.entityTypes].map((item) => {
    return {
        display: item.name,
        value: item.id.toString(),
    };
});

export const BorrowerPage: React.FC<BorrowerPageProps> = ({ borrower, actions }) => {
    const navigate = useNavigate();
    const { apiId } = useParams<{ apiId: string }>();
    const { authentication, isLocEnabled, requiresESign, logout } = useAuthentication();
    const [validation, setValidation] = useState<FormValidationMetadata>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');
    const [complete, setComplete] = useState<boolean>(false);
    const [secondQuestion, setSecondQuestion] = useState<boolean>(false);
    const [disableBorrower] = useState<boolean>(false);

    const [duplicateEntity, set_duplicateEntity] = useState<boolean>(false);
    const [isConfirmModalOpen, set_isConfirmModalOpen] = useState<boolean>(false);
    const [entityValidations, set_entityValidations] = useState<Store.CrossStore.ValidationMessagesResult>(undefined);

    const [entityList, set_entityList] = useState<Store.CrossStore.EntityListResult>();
    const [entitySelectList, set_entitySelectList] = useState<StyledRowSelectorOptions[]>([]);

    const { search, pathname } = useLocation();
    const params = useMemo(() => {
        return new URLSearchParams(search);
    }, [search]);
    const token = params.get('token');

    useEffect(() => {
        const loanType = params.get('loanType');
        if (loanType) {
            actions?.onChangeLoanType(parseInt(loanType));
        }

        const zipCode = params.get('zipCode');
        if (zipCode) actions?.onChangeEntityAddressZipcode(zipCode);

        const city = params.get('city');
        if (city) actions?.onChangeEntityAddressCity(city);

        const state = params.get('state');
        if (state) actions?.onChangeEntityAddressState(state);

        const street1 = params.get('street1');
        if (street1) actions?.onChangeEntityAddressStreet1(street1);

        const street2 = params.get('street2');
        if (street2) actions?.onChangeEntityAddressStreet2(street2);

        const borrowerId = parseInt(params.get('borrowerId'));
        if (borrowerId) actions?.onChangeBorrowerId(borrowerId);

        const entityId = params.get('entityId');
        if (entityId) actions?.onChangeEntityName(entityId);

        const borrowerEntity = params.get('borrowerEntity');
        if (borrowerEntity) actions?.onChangeEntityName(borrowerEntity);

        const stateIncorporated = params.get('entityStateIncorporated');
        if (stateIncorporated) actions?.onChangeEntityStateIncorporated(stateIncorporated);

        const masterNoteId = parseInt(params.get('masterNoteId'));
        if (masterNoteId) actions?.onChangeMasterNoteId(masterNoteId);

        const entityTypeId = params.get('entityTypeId');
        if (entityTypeId) actions?.onChangeConfirmEntity(parseInt(entityTypeId));
    }, [actions, params]);

    const loanTypeOptions = [
        ...LoanTypeOptions.filter((t) => t.id && t.id !== LoanType.MasterNote).sort(
            ({ sortOrder: a }, { sortOrder: b }) => a - b
        ),
        { id: 1000000, name: 'Liquidity Access (Coming Soon)' },
    ].map((lt) => {
        let margin = !lt.id ? '0 0 15px 0' : '0';
        switch (lt.id) {
            case LoanType.SubNoteConstruction:
            case LoanType.BuildRent:
            case LoanType.SubNoteFixFlip:
            case LoanType.Land:
                margin = '0 0 15px 0';
                break;
        }

        return {
            value: lt.id,
            label: lt.id === LoanType.BuildRent ? 'Build-Rent or Fix-Rent' : lt.name,
            padding: '5px',
            margin,
            bgColor: '#f7f7f7',
            isDisabled:
                lt.name === 'Liquidity Access (Coming Soon)' ||
                (lt.id === LoanType.SubNoteConstruction && !isLocEnabled) ||
                (lt.id === LoanType.SubNoteFixFlip && !isLocEnabled),
        };
    });

    const findLoanTypeOption = (loanType) => {
        return loanTypeOptions.find((elm) => elm.value === loanType);
    };

    const findEntityListOption = (value) => {
        return entitySelectList.find((elm) => elm.value === value);
    };

    const loanTypeStyles = {
        option: (provided, state) => ({
            ...provided,
            //fontWeight: state.isSelected ? "bold" : "normal",
            padding: state.data.padding,
            margin: state.data.margin,
            background: state.isSelected ? '#124da5' : state.data.bgColor,
        }),
    };

    useEffect(() => {
        const _loadBorrowerList = async () => {
            const entityList = await actions.getEntityList();

            set_entityList(entityList);

            const isLoc = [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(borrower.loanType);
            const ddListValues = isLoc ? entityList.result.filter((e) => e.masterNoteId > 0) : entityList.result;

            const dropDownList = ddListValues.map((borrower) => {
                return {
                    label: `${borrower.entityName} (${borrower.entityStateIncorporated})`,
                    value: borrower.entityId,
                    padding: '5px',
                    bgColor: '#f7f7f7',
                    isDisabled: false,
                };
            });

            if (!isLoc) {
                //Add empty entry
                dropDownList.unshift({
                    label: newBorrowerOptionText,
                    value: 0,
                    padding: '5px',
                    bgColor: '#f7f7f7',
                    isDisabled: false,
                });
            }
            set_entitySelectList(dropDownList);
        };
        _loadBorrowerList();

        // if (borrower.loanType) {
        //   const isLoc = [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(borrower.loanType);
        //   setDisableBorrower(isLoc);
        // }
    }, [actions, authentication, borrower.loanType]);

    useEffect(() => {
        const user = authentication?.user;
        if (user) {
            actions.onChangePrincipalName({
                first: user.firstName,
                middle: user.middleName,
                last: user.lastName,
            });
            actions.onChangePrincipalEmail(user.email);
            actions.onChangePrincipalPhone(user.mobilePhone ?? user.workPhone);
            actions.onChangePrincipalSecondaryEmail(user.secondaryEmail);
        }
    }, [authentication, actions]);

    useEffect(() => {
        let canceled = false;

        (async () => {
            setLoading(true);
            try {
                if (apiId) {
                    const isComplete = await actions.loadStateFromApi(apiId, token);
                    if (!canceled) {
                        setComplete(isComplete);
                    }
                } else {
                    if (authentication?.username) {
                        await actions.loadBorrowerStateFromUserApplications(authentication?.username);
                    }
                }

                if (authentication?.username) {
                    await actions.getUserApplications(authentication?.username);
                }

                setLoading(false);
            } catch (err) {
                if (!canceled) {
                    setLoadError(err);
                    setLoading(false);
                }
            }
        })();

        return () => {
            canceled = true;
        };
    }, [apiId, actions, authentication?.username, token]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('Error - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: `<div style="color:darkred;">${loadError}</div><br/><div>If possible, please fix and try again.</div>`,
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, pathname]);

    const onQuestionValidate = useCallback((selector: string, passing: boolean, message: string) => {
        setValidation((state) => {
            const record = {
                ...state[selector],
                passing: passing,
                failureMessage: message,
            };
            return {
                ...state,
                [selector]: record,
            };
        });
    }, []);

    const isValidEntityType = (value) => {
        //if (value === null || value === undefined) return false;
        return (
            value >= Store.BorrowerStore.Types.entityTypesConst.LLC &&
            value <= Store.BorrowerStore.Types.entityTypesConst.CCorporation
        );
    };

    const selectFirstLocEntity = (entityList: EntityListResult) => {
        if (!entityList) {
            return;
        }

        const locEntities = entityList.result.filter((e) => e.masterNoteId > 0);
        if (locEntities.length > 0) {
            const entity = entityList.result[0];
            actions?.onChangeMasterNoteId(entity.masterNoteId);
            actions?.onChangeBorrowerId(borrower.id);
            actions?.onChangeEntityId(entity.entityId);
            actions?.onChangeEntityName(entity.entityName);
            actions?.onChangeEntityStateIncorporated(entity.entityStateIncorporated);
            actions?.onChangeConfirmEntity(entity.entityType ?? undefined);
            actions?.onChangeEntityAddressStreet1(entity.address.street1);
            actions?.onChangeEntityAddressStreet2(entity.address.street2);
            actions?.onChangeEntityAddressCity(entity.address.city);
            actions?.onChangeEntityAddressState(entity.address.state);
            actions?.onChangeEntityAddressZipcode(entity.address.zipCode);
        }
    };

    const onLoanTypeChanged = async (loanType) => {
        if (apiId && borrower.loanType != loanType) {
            confirmModal({
                title: 'Change Application Type',
                message: `Changing the type will reset your application. You may be required to fill out information again. Would you like to continue?`,
                onConfirm: () => {
                    let href = '';
                    if (![LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(borrower.loanType)) {
                        href = '/borrower/?' + `loanType=${loanType}`;
                    } else {
                        href =
                            '/borrower/?' +
                            `loanType=${loanType}` +
                            `&zipCode=${borrower?.entity?.address?.zipCode}&` +
                            `city=${borrower?.entity?.address?.city}&` +
                            `state=${borrower?.entity?.address?.state}&` +
                            `street1=${borrower?.entity?.address?.street1}&` +
                            `street2=${borrower?.entity?.address?.street2}&` +
                            `borrowerId=${borrower?.id}&` +
                            `borrowerEntity=${borrower?.entity?.name}&` +
                            `entityTypeId=${borrower?.entity?.typeId}`;
                    }
                    window.location.href = href;
                },
            });
        } else {
            actions?.onChangeLoanType(loanType);
            const isLoc = [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(loanType);
            // setDisableBorrower(isLoc);
            if (isLoc) {
                selectFirstLocEntity(entityList);
            }
        }
    };

    const onSelectedEntityChanged = async (entityId: number) => {
        if (entityId == 0) {
            actions?.onChangeEntityId(null);
            actions?.onChangeEntityName('');
            actions?.onChangeEntityStateIncorporated('');
            actions?.onChangeConfirmEntity(undefined);
            actions?.onChangeEntityAddressStreet1('');
            actions?.onChangeEntityAddressStreet2('');
            actions?.onChangeEntityAddressCity('');
            actions?.onChangeEntityAddressState('');
            actions?.onChangeEntityAddressZipcode('');
        } else {
            const entity = entityList.result.find((b) => b.entityId == entityId);
            const isLoc = [LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(borrower.loanType);
            if (isLoc) {
                actions?.onChangeMasterNoteId(entity.masterNoteId);
            }
            actions?.onChangeEntityId(entity.entityId);
            actions?.onChangeEntityName(entity.entityName);
            actions?.onChangeEntityStateIncorporated(entity.entityStateIncorporated);
            actions?.onChangeConfirmEntity(entity.entityType ?? undefined);
            actions?.onChangeEntityAddressStreet1(entity.address.street1);
            actions?.onChangeEntityAddressStreet2(entity.address.street2);
            actions?.onChangeEntityAddressCity(entity.address.city);
            actions?.onChangeEntityAddressState(entity.address.state);
            actions?.onChangeEntityAddressZipcode(entity.address.zipCode);
        }
    };

    const validateEntity = async (): Promise<boolean> => {
        set_duplicateEntity(false);
        set_isConfirmModalOpen(false);
        set_entityValidations(undefined);

        //Validate the Entity Name is not in the list of Options available
        const id = borrower?.entity?.id ?? 0;
        const entName = (borrower?.entity?.name ?? '').trim();
        const stateName = (borrower?.entity?.stateIncorporated ?? '').trim();
        const exists = entityList.result.some(
            (a) =>
                a.entityId != id &&
                a.entityName.trim().toLowerCase() == entName.toLowerCase() &&
                a.entityStateIncorporated.trim().toLowerCase() === stateName.toLowerCase()
        );
        if (exists) {
            set_duplicateEntity(true);
            return false;
        }

        //Validate the Entity name and state on the backend
        const validationResult = await actions.validateEntity(
            borrower?.entity?.id,
            borrower?.entity?.name,
            borrower?.entity?.stateIncorporated
        );
        if (validationResult && !validationResult.hasErrors && !validationResult.hasWarnings) {
            return true;
        }

        set_entityValidations(validationResult);
        if (validationResult?.hasWarnings) {
            set_entityValidations(validationResult);
            set_isConfirmModalOpen(true);
        }
        return false;
    };

    const nextButtonClick = async () => {
        setSubmitted(true);
        const failureMessages: string[] = [];
        for (const fieldName in validation) {
            if (!validation[fieldName]?.passing) {
                /* istanbul ignore else */
                if (validation[fieldName]?.failureMessage) {
                    failureMessages.push(validation[fieldName]?.failureMessage);
                }
            }
        }

        const isOk = await validateEntity();
        if (!isOk) {
            return;
        }

        if (failureMessages.length > 0) {
            console.error('form validation failed', failureMessages);
            return;
        } else if (validEntityType(borrower?.entity?.name as string) && !isValidEntityType(borrower.entity?.typeId)) {
            actions?.onChangeConfirmEntity(Store.BorrowerStore.Types.entityTypesConst.LLC);
        } else if (
            !validEntityType(borrower?.entity?.name as string) &&
            !isValidEntityType(borrower.entity?.typeId) &&
            ![LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip].includes(borrower.loanType)
        ) {
            setSecondQuestion(true);
            return;
        }

        await saveBorrower();
    };

    const saveBorrower = async () => {
        setLoading(true);
        actions
            .saveStateToApi(apiId, false, token)
            .then((idAndToken) => {
                setLoading(false);
                navigate(`/borrower/${idAndToken.id}${idAndToken.token ? '?token=' + idAndToken.token : ''}`, { replace: true });
                navigate(`/property/${idAndToken.id}${idAndToken.token ? '?token=' + idAndToken.token : ''}`);
            })
            .catch((err) => {
                setLoading(false);
                setLoadError(err);
            });
    };

    const [submitted, setSubmitted] = useState(false);

    const handleFileSelected = useCallback(
        async (
            fileType: string,
            file: File,
            updater: (guid: string, value: Store.PropertiesStore.Types.Attachment) => unknown
        ) => {
            actions.uploadAttachment(
                apiId,
                borrower.loanType,
                '', // not attached to a specific property
                fileType as Store.PropertiesStore.Types.AttachmentType,
                file,
                updater as Store.PropertiesStore.Actions.AttachmentUpdater,
                token
            );
        },
        [actions, apiId, borrower.loanType, token]
    );

    if (loading) {
        return (
            <div
                className='overlay'
                data-testid='borrower-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }

    if (requiresESign) {
        return <Navigate to={`/signagreement`} />;
    }

    if (complete) {
        return <Navigate to={`/review/${apiId}${token ? '?token=' + token : ''}`} />;
    }

    const onConfirmApproval = () => {
        set_isConfirmModalOpen(false);
        saveBorrower();
    };

    const onCancelApproval = () => {
        set_isConfirmModalOpen(false);
    };

    return (
        <>
            <Header />
            <Callout />
            <main>
                <HomePageLink />
                <section>
                    <div>
                        <Question
                            type={QuestionType.StyledRowSelector}
                            options={loanTypeOptions}
                            selector='loanType'
                            submitted={submitted}
                            styleName='banner2'
                            styles={loanTypeStyles}
                            option={findLoanTypeOption(borrower.loanType)}
                            onChange={(loanType) => onLoanTypeChanged(loanType)}
                            onQuestionValidate={onQuestionValidate}
                            disabled={apiId != undefined}
                        >
                            Loan Type
                        </Question>
                    </div>
                    <ShowIf showIf={!!borrower?.loanType}>
                        <div>
                            <Question
                                type={QuestionType.EntitySelect}
                                options={entitySelectList}
                                selector='borrower'
                                submitted={submitted}
                                styleName='banner'
                                option={findEntityListOption(borrower?.entity?.id)}
                                onChange={(entityId) => onSelectedEntityChanged(entityId)}
                                onQuestionValidate={onQuestionValidate}
                                disabled={apiId != undefined}
                                value={borrower?.entity?.id}
                            >
                                Select Borrower
                            </Question>
                        </div>

                        <ShowIf showIf={borrower.isFirstLoan}>
                            <div className='form-group'>
                                <div className='row'>
                                    <Question
                                        type={QuestionType.YesNo}
                                        submitted={submitted}
                                        selector='firstLoadApplicationWithBfi'
                                        styleName='ungrouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.firstLoanApplicationWithBfi}
                                        onChange={actions?.onChangeFirstLoadApplicationWithBfi}
                                    >
                                        Is this your first loan application with BFI?
                                    </Question>
                                </div>
                            </div>
                        </ShowIf>
                        <ShowIf showIf={borrower.isFirstLoan}>
                            <div className='form-group'>
                                <div className='row'>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='howDidYouHear'
                                        placeholder='Referral, ad, internet search'
                                        styleName='ungrouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.howDidYouHear}
                                        onChange={actions?.onChangeHowDidYouHear}
                                    >
                                        How did you find us?
                                    </Question>
                                </div>
                            </div>
                        </ShowIf>
                        <ShowIf showIf={borrower.shouldAskLegalQuestions}>
                            <QuestionGroup
                                trigger={
                                    borrower?.currentlyQuestions?.defendantInLegalActions ||
                                    borrower?.currentlyQuestions?.delinquentOnDebts ||
                                    borrower?.currentlyQuestions?.delinquentOnTaxes ||
                                    borrower?.currentlyQuestions?.financiallyWorseOff ||
                                    borrower?.currentlyQuestions?.involvedInJudgements
                                }
                            >
                                <Legend>
                                    Currently, are you, your organization, and/or an owner or officer of your organization:
                                </Legend>
                                <Question
                                    type={QuestionType.YesNo}
                                    submitted={submitted}
                                    selector='defendantInLegalActions'
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.currentlyQuestions?.defendantInLegalActions}
                                    onChange={actions?.onChangeDefendantInLegalActions}
                                >
                                    A defendant in any suits or legal actions?
                                </Question>
                                <Question
                                    type={QuestionType.YesNo}
                                    submitted={submitted}
                                    selector='involvedInJudgement'
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.currentlyQuestions?.involvedInJudgements}
                                    onChange={actions?.onChangeInvolvedInJudgement}
                                >
                                    Involved in any judgements, liens or surety actions?
                                </Question>
                                <Question
                                    type={QuestionType.YesNo}
                                    submitted={submitted}
                                    selector='delinquentOnDebts'
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.currentlyQuestions?.delinquentOnDebts}
                                    onChange={actions?.onChangeDelinquentOnDebts}
                                >
                                    Delinquent on any real estate debts?
                                </Question>
                                <Question
                                    type={QuestionType.YesNo}
                                    submitted={submitted}
                                    selector='delinquentOnTaxes'
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.currentlyQuestions?.delinquentOnTaxes}
                                    onChange={actions?.onChangeDelinquentOnTaxes}
                                >
                                    Delinquent on any taxes?
                                </Question>
                                <Question
                                    type={QuestionType.YesNo}
                                    submitted={submitted}
                                    selector='financiallyWorseOff'
                                    onQuestionValidate={onQuestionValidate}
                                    showIf={!borrower?.firstLoanApplicationWithBfi}
                                    value={borrower?.currentlyQuestions?.financiallyWorseOff}
                                    onChange={actions?.onChangeFinanciallyWorseOff}
                                >
                                    Financially worse off, to a material degree, relative to the time of your last loan
                                    application with BFI?
                                </Question>
                                <IfNeedsAdditional>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='currentlyQuestionsExplanations'
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.currentlyQuestions?.explanations}
                                        onChange={actions?.onChangeCurrentlyQuestionsExplanations}
                                    >
                                        Briefly explain any items you answered yes:
                                    </Question>
                                </IfNeedsAdditional>
                            </QuestionGroup>
                        </ShowIf>
                        <div className='form-group visual-group'>
                            <div className='row form-group-no-after columns small-12'>
                                <Question
                                    type={QuestionType.RequiredEntityName}
                                    submitted={submitted}
                                    selector='borrowerEntity'
                                    placeholder='ABC Borrowing Company, LCC'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.entity?.name}
                                    onChange={actions?.onChangeEntityName}
                                    disabled={disableBorrower}
                                    required={true}
                                >
                                    Borrower Entity
                                </Question>
                                <Question
                                    type={QuestionType.EntityStateIncorporated}
                                    selector='entityStateIncorporated'
                                    styleName='ungrouped'
                                    submitted={submitted}
                                    onQuestionValidate={onQuestionValidate}
                                    value={borrower?.entity?.stateIncorporated}
                                    values={stateSelectorValues}
                                    onChange={actions?.onChangeEntityStateIncorporated}
                                    disabled={disableBorrower}
                                    required={true}
                                >
                                    State Incorporated
                                </Question>
                                <ShowIf showIf={duplicateEntity}>
                                    <div
                                        data-validation-additional-message='Duplicate Entity'
                                        style={{
                                            transition: '0.28s',
                                            color: 'red',
                                        }}
                                    >
                                        The entered Entity matches one available in the list please select from the list.
                                    </div>
                                </ShowIf>

                                <ShowIf showIf={entityValidations?.hasErrors}>
                                    {entityValidations?.messages?.map((m) => {
                                        return (
                                            <div>
                                                <span style={{ color: 'red' }}>
                                                    <FontAwesomeIcon
                                                        icon={faExclamationTriangle}
                                                        style={{ color: 'red' }}
                                                    />
                                                    &nbsp;
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: m.message,
                                                        }}
                                                    />
                                                </span>
                                            </div>
                                        );
                                    })}
                                </ShowIf>

                                <ShowIf
                                    showIf={
                                        borrower?.entity?.name &&
                                        (secondQuestion ||
                                            borrower?.entity?.typeId >= Store.BorrowerStore.Types.entityTypesConst.NotApplicable)
                                    }
                                >
                                    <Question
                                        type={QuestionType.RequiredConfirmEntity}
                                        submitted={submitted}
                                        selector='entityTypeId'
                                        styleName='ungrouped'
                                        values={entityTypes}
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.entity?.typeId}
                                        onChange={(id) => {
                                            actions?.onChangeConfirmEntity(parseInt(id));
                                        }}
                                        disabled={disableBorrower}
                                        required={true}
                                    >
                                        Confirm Type of Entity
                                    </Question>
                                </ShowIf>
                            </div>
                        </div>

                        <div className='form-group visual-group'>
                            <fieldset>
                                <legend className='header'>Business Mailing Address</legend>
                                <div className='row'>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='businessStreetAddressStreet1'
                                        placeholder='123 Main Street'
                                        styleName='grouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.entity?.address?.street1}
                                        onChange={actions?.onChangeEntityAddressStreet1}
                                        disabled={disableBorrower}
                                        required={true}
                                    >
                                        <span className='sr-only'>Business</span> Street Address
                                    </Question>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='businessStreetAddressStreet2'
                                        placeholder='Suite 123'
                                        styleName='grouped'
                                        value={borrower?.entity?.address?.street2}
                                        onChange={actions?.onChangeEntityAddressStreet2}
                                        disabled={disableBorrower}
                                    >
                                        <span className='sr-only'>Business Street Address, Line 2</span>
                                    </Question>
                                </div>
                                <div className='row'>
                                    <div className='columns small-6 medium-4'>
                                        <Question
                                            type={QuestionType.Zipcode}
                                            submitted={submitted}
                                            selector='businessAddressZipcode'
                                            placeholder='01234'
                                            styleName='grouped'
                                            onQuestionValidate={onQuestionValidate}
                                            value={borrower?.entity?.address?.zipCode}
                                            onChange={actions?.onChangeEntityAddressZipcode}
                                            disabled={disableBorrower}
                                            required={true}
                                        >
                                            <span className='sr-only'>Business</span> Zip
                                        </Question>
                                    </div>
                                    <div className='columns small-12 medium-4'>
                                        <Question
                                            type={QuestionType.Text}
                                            submitted={submitted}
                                            selector='businessAddressCity'
                                            placeholder='Hamilton'
                                            styleName='grouped'
                                            onQuestionValidate={onQuestionValidate}
                                            value={borrower?.entity?.address?.city}
                                            onChange={actions?.onChangeEntityAddressCity}
                                            disabled={disableBorrower}
                                            required={true}
                                        >
                                            <span className='sr-only'>Business</span> City
                                        </Question>
                                    </div>
                                    <div className='columns small-6 medium-4'>
                                        <Question
                                            type={QuestionType.ColumnSelect}
                                            selector='businessAddressState'
                                            styleName='grouped'
                                            submitted={submitted}
                                            onQuestionValidate={onQuestionValidate}
                                            value={borrower?.entity?.address?.state}
                                            values={stateSelectorValues}
                                            onChange={actions?.onChangeEntityAddressState}
                                            disabled={disableBorrower}
                                            required={true}
                                        >
                                            <span className='sr-only'>Business</span> State
                                        </Question>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <ShowIf showIf={borrower?.loanType === LoanType.Rental || borrower?.loanType === LoanType.BuildRent}>
                            <h2
                                style={{
                                    marginTop: '1rem',
                                    maxWidth: '100%',
                                    transform: 'translateX(0)',
                                }}
                            >
                                Property Management
                            </h2>
                            <div className='form-group'>
                                <div className='row'>
                                    <Question
                                        type={QuestionType.YesNo}
                                        submitted={submitted}
                                        selector='usingPropertyManagement'
                                        styleName='ungrouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={borrower?.usingPropertyManagementCompany}
                                        onChange={actions?.onChangeUsingPropertyManagement}
                                        required={true}
                                    >
                                        Will you use a property management company?
                                    </Question>
                                </div>
                            </div>
                            <ShowIf showIf={borrower?.usingPropertyManagementCompany}>
                                <div className='form-group visual-group'>
                                    <fieldset>
                                        <legend className='header'>Property Management Company</legend>
                                        <div
                                            className='row'
                                            style={{ paddingBottom: '10px' }}
                                        >
                                            <div className='columns small-12 medium-6'>
                                                <Question
                                                    type={QuestionType.Text}
                                                    submitted={submitted}
                                                    selector='propertyManagementCompanyName'
                                                    placeholder='The Property Company, LCC'
                                                    styleName='grouped'
                                                    onQuestionValidate={onQuestionValidate}
                                                    value={borrower?.propertyManagementCompany?.name}
                                                    onChange={actions?.onChangePropertyManagementCompanyName}
                                                    required={true}
                                                >
                                                    <span className='sr-only'>Property Management</span> Company name
                                                </Question>
                                            </div>
                                            <div className='columns small-12 medium-6'>
                                                <Question
                                                    type={QuestionType.OptionalUrl}
                                                    submitted={submitted}
                                                    selector='propertyManagementCompanyWebsite'
                                                    placeholder='www.somecompany.com'
                                                    styleName='grouped'
                                                    onQuestionValidate={onQuestionValidate}
                                                    value={borrower?.propertyManagementCompany?.website}
                                                    onChange={actions?.onChangePropertyManagementCompanyWebsite}
                                                >
                                                    <span className='sr-only'>Property Management</span> Website
                                                </Question>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <FileOrFormQuestion
                                                submitted={submitted}
                                                selector='managementAgreement'
                                                styleName='ungrouped'
                                                onQuestionValidate={onQuestionValidate}
                                                value={borrower?.propertyManagementCompany?.managementAgreement}
                                                onChangeQualifier={undefined}
                                                onChange={actions?.onChangeManagementAgreement}
                                                fileType={'management agreement'}
                                                onFileSelected={handleFileSelected}
                                                required={true}
                                            >
                                                Management Agreement
                                            </FileOrFormQuestion>
                                        </div>
                                    </fieldset>
                                </div>
                            </ShowIf>
                        </ShowIf>
                    </ShowIf>
                </section>
                <div className='button-group'>
                    {/* No back button needed */}
                    <ShowIf showIf={!!borrower?.loanType}>
                        <button
                            id='nextButton'
                            onClick={nextButtonClick}
                            className='button'
                        >
                            Next
                        </button>
                    </ShowIf>
                </div>
            </main>
            <ConfirmModal
                confirmButtonText={'Yes, continue'}
                isOpen={isConfirmModalOpen}
                confirmCallback={onConfirmApproval}
                cancelCallback={onCancelApproval}
            >
                {entityValidations?.messages?.map((m) => {
                    return (
                        <div>
                            <span>
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    style={{ color: 'darkorange' }}
                                />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: m.message,
                                    }}
                                />
                            </span>
                        </div>
                    );
                })}
            </ConfirmModal>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    borrower: state.borrower,
    completedApplications: state.user?.completedApplications ?? [],
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            onChangeLoanType: (newvalue: LoanType) => dispatch(Actions.updateLoanType(newvalue)),
            onChangeFirstLoadApplicationWithBfi: (newvalue: boolean | undefined) =>
                dispatch(Actions.updateFirstLoanApplicationWithBfi(newvalue)),
            onChangeHowDidYouHear: (newvalue: string | undefined) => dispatch(Actions.updateHowDidYouHear(newvalue)),
            onChangeInvolvedInJudgement: (newvalue: boolean | undefined) => dispatch(Actions.updateInvolvedInJudgement(newvalue)),
            onChangeDelinquentOnDebts: (newvalue: boolean | undefined) => dispatch(Actions.updateDelinquentOnDebts(newvalue)),
            onChangeDelinquentOnTaxes: (newvalue: boolean | undefined) => dispatch(Actions.updateDelinquentOnTaxes(newvalue)),
            onChangeFinanciallyWorseOff: (newvalue: boolean | undefined) => dispatch(Actions.updateFinanciallyWorseOff(newvalue)),
            onChangeCurrentlyQuestionsExplanations: (newvalue: string | undefined) =>
                dispatch(Actions.updateCurrentlyQuestionsExplanations(newvalue)),
            onChangeDefendantInLegalActions: (newvalue: boolean | undefined) =>
                dispatch(Actions.updateDefendantInLegalActions(newvalue)),
            onChangeUsingPropertyManagement: (newvalue: boolean | undefined) =>
                dispatch(Actions.updateUsingPropertyManagement(newvalue)),
            onChangePropertyManagementCompanyName: (newvalue: string | undefined) =>
                dispatch(Actions.updatePropertyManagementCompanyName(newvalue)),
            onChangePropertyManagementCompanyWebsite: (newvalue: string | undefined) =>
                dispatch(Actions.updatePropertyManagementCompanyWebsite(newvalue)),
            onChangePropertyManagementCompanyContactName: (newvalue: Store.BorrowerStore.Types.FullName) =>
                dispatch(Actions.updatePropertyManagementCompanyContactName(newvalue)),
            onChangePropertyManagementCompanyContactEmail: (newvalue: string | undefined) =>
                dispatch(Actions.updatePropertyManagementCompanyContactEmail(newvalue)),
            onChangePropertyManagementCompanyContactPhone: (newvalue: string | undefined) =>
                dispatch(Actions.updatePropertyManagementCompanyContactPhone(newvalue)),
            onChangeContactInfoSameAsPropertyContact: (newvalue: boolean | undefined) =>
                dispatch(Actions.updateContactInfoSameAsPropertyContact(newvalue)),
            onChangePrincipalName: (newvalue: Store.BorrowerStore.Types.FullName) =>
                dispatch(Actions.updatePrincipalName(newvalue)),
            onChangePrincipalEmail: (newvalue: string | undefined) => dispatch(Actions.updatePrincipalEmail(newvalue)),
            onChangePrincipalSecondaryEmail: (newvalue: string | undefined) =>
                dispatch(Actions.updatePrincipalSecondaryEmail(newvalue)),
            onChangePrincipalPhone: (newvalue: string | undefined) => dispatch(Actions.updatePrincipalPhone(newvalue)),
            onChangeBorrowerId: (newvalue: number | undefined) => dispatch(Actions.updateBorrowerId(newvalue)),
            onChangeEntityId: (newvalue: number | undefined) => dispatch(Actions.updateEntityId(newvalue)),
            onChangeEntityName: (newvalue: string | undefined) => dispatch(Actions.updateEntityName(newvalue)),
            onChangeEntityStateIncorporated: (newvalue: string | undefined) =>
                dispatch(Actions.updateEntityStateIncorporated(newvalue)),
            onChangeMasterNoteId: (newvalue: number | undefined) => dispatch(Actions.updateMasterNoteId(newvalue)),
            onChangeConfirmEntity: (newvalue: number | undefined) => dispatch(Actions.updateConfirmEntity(newvalue)),
            onChangeEntityAddressStreet1: (newvalue: string | undefined) =>
                dispatch(Actions.updateEntityAddressStreet1(newvalue)),
            onChangeEntityAddressStreet2: (newvalue: string | undefined) =>
                dispatch(Actions.updateEntityAddressStreet2(newvalue)),
            onChangeEntityAddressCity: (newvalue: string | undefined) => dispatch(Actions.updateEntityAddressCity(newvalue)),
            onChangeEntityAddressState: (newvalue: string | undefined) => dispatch(Actions.updateEntityAddressState(newvalue)),
            onChangeEntityAddressZipcode: (newvalue: string | undefined) =>
                dispatch(Actions.updateEntityAddressZipcode(newvalue)),
            loadStateFromApi: (apiId: string, token: string) => dispatch(Store.CrossStore.loadStateFromApi(apiId, null, token)),
            loadBorrowerStateFromUserApplications: (email: string) =>
                dispatch(Store.CrossStore.loadBorrowerStateFromUserApplications(email)),
            saveStateToApi: (apiId: string, complete: false, token: string) =>
                dispatch(Store.CrossStore.saveStateToApi(apiId, complete, token)),
            getUserApplications: (email: string) => dispatch(Store.CrossStore.getUserApplications(email)),
            getEntityList: () => Store.CrossStore.getEntityList(),
            validateEntity: (id: number, name: string, state: string) => Store.CrossStore.validateEntity(id, name, state),
            onChangeGuestAcknowledgedNoPersonalLoans: (newvalue: boolean | undefined) =>
                dispatch(Actions.updateGuestAcknowledgedNoPersonalLoans(newvalue)),
            onChangeManagementAgreement: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(Actions.updateManagementAgreement(guid, value)),
            uploadAttachment: (
                apiId: string,
                loanType: LoanType,
                propertyGuid: string,
                fileType: Store.PropertiesStore.Types.AttachmentType,
                file: File,
                updater: Store.PropertiesStore.Actions.AttachmentUpdater,
                token: string
            ) => dispatch(Store.CrossStore.uploadAttachment(apiId, loanType, propertyGuid, fileType, file, updater, token)),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(BorrowerPage);
